import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"

function normalizeSocialAccountsPayload(payload) {
  if (Array.isArray(payload)) return payload
  if (payload && typeof payload === "object") {
    const inner = payload.data ?? payload.accounts ?? payload.content ?? payload.results
    if (Array.isArray(inner)) return inner
  }
  return []
}

export const fetchSocialNetworkAccounts = createAsyncThunk(
  "socialNetworkAccounts/fetch",
  async (clientId, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required")
      }

      if (!clientId) {
        throw new Error("Client ID is required")
      }

      const data = await get(`social-network-accounts/clients/${clientId}`)
      return normalizeSocialAccountsPayload(data)
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch social network accounts")
    }
  },
  {
    condition: (clientId, { getState }) => {
      const id = clientId != null ? String(clientId) : ""
      if (!id) return false
      const slice = getState().socialNetworkAccounts
      const same = String(slice.fetchedForClientId ?? "") === id
      if (slice.status === "loading" && same) return false
      if (slice.status === "succeeded" && same) return false
      return true
    },
  },
)

const initialState = {
  accounts: [],
  status: "idle", // idle | loading | succeeded | failed
  error: null,
  /** Last clientId whose `accounts` payload is shown (avoids stale data when switching properties). */
  fetchedForClientId: null,
}

const socialNetworkAccountsSlice = createSlice({
  name: "socialNetworkAccounts",
  initialState,
  reducers: {
    clearAccounts: (state) => {
      state.accounts = []
      state.status = "idle"
      state.error = null
    },
    clearError: (state) => {
      state.error = null
    },
    invalidateSocialNetworkAccounts: (state) => {
      state.accounts = []
      state.status = "idle"
      state.error = null
      state.fetchedForClientId = null
    },
    resetOnLogout: (state) => {
      return initialState
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSocialNetworkAccounts.pending, (state, action) => {
        const nextId =
          action.meta.arg != null ? String(action.meta.arg) : null
        if (nextId == null) return
        const prevId =
          state.fetchedForClientId != null
            ? String(state.fetchedForClientId)
            : null
        const isSameClientRefresh =
          prevId !== null && prevId === nextId && state.status === "succeeded"
        if (isSameClientRefresh) {
          state.fetchedForClientId = nextId
          return
        }
        state.status = "loading"
        state.error = null
        if (prevId !== null && prevId !== nextId) {
          state.accounts = []
        }
        state.fetchedForClientId = nextId
      })
      .addCase(fetchSocialNetworkAccounts.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.accounts = Array.isArray(action.payload) ? action.payload : []
        state.fetchedForClientId =
          action.meta.arg != null ? String(action.meta.arg) : null
      })
      .addCase(fetchSocialNetworkAccounts.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
        state.accounts = []
        state.fetchedForClientId = null
      })
  },
})

export const {
  clearAccounts,
  clearError,
  invalidateSocialNetworkAccounts,
  resetOnLogout,
} = socialNetworkAccountsSlice.actions

export default socialNetworkAccountsSlice.reducer
